import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean, useCopyToClipboard } from 'react-use'

import { toasted } from '@/lib/toast'
import { useTwoFaContext } from '@/context/TwoFaContext'

/**
 * `useSetup2FA` Hook
 *
 * This is a custom hook used to aid in the setup of two-factor authentication (2FA) for a user.
 * It provides functionalities to handle OTP input, enable 2FA, and manage relevant states.
 *
 * @param {Object} params - The parameters for the hook.
 * @param {boolean} params.onlyInput - If set to true, the setup process will only require the user to input the OTP, skipping other steps like showing the QR code or backup codes.
 *
 * @returns {Object} Returns an object containing:
 * - twoFactorOtp: A state variable for storing the OTP entered by the user.
 * - setTwoFactorOtp: A setter function for the twoFactorOtp state.
 * - errOtp: An error message related to the OTP.
 * - setErrOtp: A setter function for the errOtp state.
 * - isLoading: A state variable indicating if the process is loading.
 * - setIsLoading: A setter function for the isLoading state.
 * - handleEnable: A function that attempts to enable 2FA using the entered OTP.
 * - handleGetQr: A function to fetch and display the QR code for 2FA setup.
 * - clipBoardState: A state variable containing the clipboard state.
 * - toasted: A function to show toast notifications.
 * - setValue: A function to set multiple states at once.
 *
 */
const useSetup2FA = ({ onlyInput }: { onlyInput?: boolean }) => {
  const { t } = useTranslation()
  const { value, setValue, handleEnable } = useTwoFaContext()

  const [twoFactorOtp, setTwoFactorOtp] = useState('')
  const hasOtpError = twoFactorOtp.length !== 6
  const qrUrl = value.qr ? URL.createObjectURL(value.qr) : ''
  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)
  const [clipBoardState, setClipBoard] = useCopyToClipboard()
  const [errOtp, setErrOtp] = useState('')

  // This effect runs whenever the clipboard state or the translation function changes.
  useEffect(() => {
    // If there was an error copying to the clipboard, show a toast notification with the error message.
    if (clipBoardState.error) {
      toasted(
        t('Unable to copy value: {{error}}', {
          error: clipBoardState.error.message,
        })
      )
    } else {
      // If the value was successfully copied to the clipboard, show a toast notification indicating success.
      if (clipBoardState.value) {
        toasted(t('Secret copied!'))
      }
    }
  }, [clipBoardState, t]) // Depend on clipBoardState and t to rerun the effect when they change

  // This function handles form submission
  const handleSubmit = () => {
    // Set loading state for the button
    setIsBtnLoading(true)
    // Call the handleEnable function with the OTP and a set of callbacks
    handleEnable(twoFactorOtp, {
      onSuccess: () => {
        // If successful, hide the setup and potentially show the backup based on whether onlyInput is true or not
        setValue({ showSetup: false, showBackup: !onlyInput })
      },
      onFailed: () =>
        // If the operation failed, set an error message indicating that the code is invalid or has already been used
        setErrOtp(t('Code is invalid or has already been used')),
      onFinally: () =>
        // Regardless of success or failure, stop showing the button loading state
        setIsBtnLoading(false),
    })
  }

  return {
    qrUrl,
    value,
    twoFactorOtp,
    isBtnLoading,
    errOtp,
    hasOtpError,
    setValue,
    setClipBoard,
    setTwoFactorOtp,
    handleSubmit,
    t,
  }
}

export default useSetup2FA
